import { hasFeatureCanary, PlatformDependencies } from '@citrite/workspace-ui-platform';
import Cookies from 'js-cookie';
import URI from 'urijs';
import { performance } from 'Components/performance';
import { FeatureFlag } from 'Environment/FeatureFlag';
import { showPreventIcaDownloadsModal } from 'Environment/launchResource/PreventIcaDownloadsModal';
import {
	modifyIcaFileContent,
	SectionType,
	triggerFileDownload,
} from 'Environment/utils';
import { CSRF_TOKEN } from 'javascript/interceptors/Headers';
import { container } from 'Workspace/DependencyManagement';

let iframe: HTMLIFrameElement;
interface IcaJsonResponse {
	status: string;
	errorId?: string;
	fileFetchUrl?: string | null;
}

export function downloadICAFile(
	url: string,
	handleLaunchStatus: (contents: string) => void,
	preventIcaDownloads: string,
	resourceName: string
) {
	if (preventIcaDownloads === 'true') {
		showPreventIcaDownloadsModal(resourceName);
		return Promise.resolve();
	}
	const workspaceConfiguration = container
		.resolve(PlatformDependencies.WorkspaceConfiguration)
		.get();
	const isIcaDownloadUsingFetchEnabled = hasFeatureCanary(
		workspaceConfiguration,
		FeatureFlag.EnableIcaDownloadUsingFetch
	);

	const launchUrl = new URI(url).setSearch('launchId', Date.now().toString()).toString();
	return isIcaDownloadUsingFetchEnabled
		? downloadICAFileWithFetch(launchUrl, handleLaunchStatus, resourceName)
		: downloadICAFileViaIframe(launchUrl, handleLaunchStatus);
}

function downloadICAFileViaIframe(
	launchURL: string,
	handleLaunchStatus: (contents: string) => void
) {
	if (!iframe) {
		iframe = document.createElement('iframe');
		iframe.style.display = 'none';
		document.body.appendChild(iframe);
	}

	const csrfToken = Cookies.get('CsrfToken');
	const urlWithCsrfToken = csrfToken
		? new URI(launchURL).addSearch('CsrfToken', csrfToken).toString()
		: launchURL;

	iframe.src = urlWithCsrfToken;
	iframe.addEventListener('load', onLoad);

	function onLoad() {
		iframe.removeEventListener('load', onLoad);
		const contents = iframe.innerText;
		if (contents) {
			// The iframe contents are reported as empty when an ICA launch is triggered via file type association.
			return handleLaunchStatus(contents);
		}
	}

	return Promise.resolve();
}

function downloadICAFileWithFetch(
	launchURL: string,
	handleLaunchStatus: (contents: string) => void,
	resourceName: string
) {
	const uri = new URI(launchURL);
	const pathSegments = uri.segment();
	const filename = pathSegments[pathSegments.length - 1] || `${resourceName}.ica`;
	const csrfToken = Cookies.get('CsrfToken');

	return (
		csrfToken
			? fetch(launchURL, {
					headers: {
						[CSRF_TOKEN]: csrfToken,
					},
			  })
			: fetch(launchURL)
	)
		.then(response => {
			if (!response.ok) {
				return Promise.reject(new Error(`${response}`));
			}
			return response.text();
		})
		.then((responseText: string) => {
			let jsonResponse: IcaJsonResponse;
			try {
				jsonResponse = JSON.parse(responseText);
			} catch (parseError) {
				const icaFileDownloadTime = performance.getICAFileDownloadTimestamp(launchURL);
				const modifiedIcaFile = modifyIcaFileContent(
					responseText,
					SectionType.SessionLaunchTelemetry,
					{
						IFDT: icaFileDownloadTime,
					}
				);
				const blob = new Blob([modifiedIcaFile], { type: 'application/x-ica' });
				triggerFileDownload(filename, blob);
				return Promise.resolve();
			}

			const jsonResponseString = JSON.stringify(jsonResponse);
			handleLaunchStatus(jsonResponseString);
			return Promise.resolve();
		})
		.catch(error => {
			return Promise.reject(error);
		});
}
